<template>
    <ul class="list-none p-0 m-0 pt-2 w-full">
        <!-- App-level navigation: Communities link -->
        <li class="mb-3">
            <a
                v-ripple
                v-tooltip.right="{
                    value: !sidebarOpen ? 'Communities' : null,
                    showDelay: 300,
                }"
                @click="navigateToTopLevelDashboard"
                class="flex align-items-center cursor-pointer p-3 hover:bg-sidebar-hover text-bluegray-100 hover:text-bluegray-50 transition-duration-150 transition-colors p-ripple no-underline"
                :class="{ 'justify-content-center': !sidebarOpen }">
                <i
                    :class="['far fa-grid-2', 'text-center']"
                    :style="sidebarOpen ? 'width: 2em' : 'width: auto'"></i>
                <span v-if="sidebarOpen" class="font-medium ml-2"
                    >Communities</span
                >
            </a>
            <div class="mx-3 my-2 border-top-1 surface-border"></div>
        </li>

        <!-- Venue Selector -->
        <li class="mb-2">
            <VenueSelector :sidebar-open="sidebarOpen" />
        </li>

        <!-- Community-level navigation routes -->
        <li v-for="menuCategory in communityRoutes" :key="menuCategory.path">
            <span
                v-if="
                    (menuCategory as any).meta &&
                    (menuCategory as any).meta.label &&
                    sidebarOpen
                "
                class="flex align-items-center p-3 border-round text-bluegray-100">
                <span class="font-medium uppercase">{{
                    (menuCategory as any).meta.label
                }}</span>
            </span>
            <ul
                v-if="(menuCategory as any).children"
                class="list-none p-0 m-0 overflow-y-hidden">
                <li
                    v-for="menuItem in (menuCategory as any).children"
                    :key="menuItem.path">
                    <router-link
                        v-if="
                            (menuItem as any).meta &&
                            (menuItem as any).meta.label
                        "
                        v-ripple
                        v-tooltip.right="{
                            value: !sidebarOpen
                                ? [(menuItem as any).meta.label, menuBadges[menuItem.name]]
                                      .filter(Boolean)
                                      .join(' · ')
                                : null,
                            showDelay: 300,
                        }"
                        :to="menuItem.fullPath || menuItem.path"
                        :class="{
                            'bg-sidebar-active': path === menuItem.fullPath || path === menuItem.path,
                            'justify-content-center': !sidebarOpen,
                        }"
                        class="flex align-items-center cursor-pointer p-3 hover:bg-sidebar-hover text-bluegray-100 hover:text-bluegray-50 transition-duration-150 transition-colors p-ripple no-underline">
                        <span class="relative inline-flex">
                            <i
                                :class="[
                                    (menuItem as any).meta.icon,
                                    'text-center',
                                ]"
                                :style="
                                    sidebarOpen ? 'width: 2em' : 'width: auto'
                                "></i>
                            <Badge
                                v-if="!sidebarOpen && menuBadges[menuItem.name]"
                                severity="danger"
                                class="nav-badge-collapsed"
                                :aria-label="menuBadges[menuItem.name]" />
                        </span>
                        <span v-if="sidebarOpen" class="font-medium ml-2">{{
                            (menuItem as any).meta.label
                        }}</span>
                        <Badge
                            v-if="sidebarOpen && menuBadges[menuItem.name]"
                            v-tooltip.right="menuBadges[menuItem.name]"
                            severity="danger"
                            class="ml-auto"
                            :aria-label="menuBadges[menuItem.name]" />
                    </router-link>
                </li>
            </ul>
        </li>
    </ul>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useIntervalFn } from "@vueuse/core";
import Badge from "primevue/badge";
import routes from "@/router/routes.js";
import { useInterfaceProperties } from "@/composables/useInterfaceProperties.js";
import { useViewContextStore } from "@/stores/viewContext";
import { useWearableSummaryStore } from "@/stores/wearableSummary";
import { EnvironmentService } from "@/utils/environment";
import { storeToRefs } from "pinia";
import VenueSelector from "./community/VenueSelector.vue";

const route = useRoute();
const path = computed(() => route.path);
const { sidebarOpen } = useInterfaceProperties();
const viewContext = useViewContextStore();
const { mode, venueId } = storeToRefs(viewContext);

const WEARABLE_SUMMARY_POLL_MS = 5 * 60 * 1000;
const wearableSummaryStore = useWearableSummaryStore();
const { lowBatteryCount } = storeToRefs(wearableSummaryStore);

// The Wearables entry only exists in operator mode, so only poll there.
const wearableSummaryPoll = useIntervalFn(
    wearableSummaryStore.fetchSummary,
    WEARABLE_SUMMARY_POLL_MS,
    { immediate: false, immediateCallback: true },
);
watch(
    mode,
    (m) => (m === "operator" ? wearableSummaryPoll.resume() : wearableSummaryPoll.pause()),
    { immediate: true },
);
// The count is per venue, so refresh it on a venue switch instead of waiting for the next poll.
watch(venueId, () => {
    if (mode.value === "operator") wearableSummaryStore.fetchSummary();
});

// Route name -> badge text; a menu item with text shows a red dot.
const menuBadges = computed<Record<string, string | undefined>>(() => ({
    "operator-wearables":
        lowBatteryCount.value > 0
            ? `${lowBatteryCount.value} low battery`
            : undefined,
}));

const navigateToTopLevelDashboard = () => {
    const topLevelUrl = EnvironmentService.buildTopLevelAppUrl();
    window.location.href = topLevelUrl;
};

// Filter to only show community-level routes matching current mode
const communityRoutes = computed(() => {
    return (routes as any[])
        .filter((routeGroup: any) => {
            // Only show routes for current mode
            return (
                routeGroup.meta?.communityLevel === true &&
                routeGroup.meta?.mode === mode.value
            );
        })
        .map((routeGroup: any) => ({
            ...routeGroup,
            children: routeGroup.children
                ?.filter((child: any) => {
                    // Show routes with labels
                    if (!child.meta?.label) return false;

                    // Exclude navigation hover items (like home, incident dashboard)
                    return !child.meta?.navigationHover;
                })
                .map((child: any) => {
                    // Generate full path with venue ID
                    let fullPath = child.path;
                    if (venueId.value && fullPath.includes(":venueId")) {
                        fullPath = fullPath.replace(":venueId", venueId.value.toString());
                    }
                    return {
                        ...child,
                        fullPath,
                    };
                }),
        }))
        .filter(
            (routeGroup: any) =>
                routeGroup.children && routeGroup.children.length > 0,
        );
});
</script>

<style scoped>
.nav-badge-collapsed {
    position: absolute;
    top: -0.25rem;
    right: -0.35rem;
}
</style>
